import { Button, Text } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { Account } from '@modules-deprecated/app/accounts/types'

import { useFieldArray, useFormContext, useWatch } from '../../../../../../../../hooks'
import type {
  ReviewEntryDataFormSchema,
  ReviewEntryLineItemFormValues,
} from '../../../../../../types/getReviewEntryDataFormSchema'
import { ReviewEntryLineItemRow } from './elements/ReviewEntryLineItemRow'
import { ReviewEntryLineItemsAmountHeader } from './elements/ReviewEntryLineItemsAmountHeader'
import { ReviewEntryLineItemsTotals } from './elements/ReviewEntryLineItemsTotals'
import { useReviewEntryLineItemCalculation } from './hooks/useReviewEntryLineItemCalculation'
import * as Styled from './styles'
import { getDefaultReviewEntryLineItem } from './utils/getDefaultReviewEntryLineItem'

interface ReviewEntryLineItemsProps {
  accounts: Account[]
  isFormReady?: boolean
  isReadOnly?: boolean
}

export const ReviewEntryLineItems = ({
  accounts,
  isFormReady = false,
  isReadOnly = false,
}: ReviewEntryLineItemsProps): ReactElement => {
  const { t } = useTranslation()
  const { control } = useFormContext<ReviewEntryDataFormSchema>()
  const currencyId = useWatch({ control, name: 'currencyId' })

  const {
    append: addLineItem,
    fields: lineItems,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: 'entryLines',
  })

  const { handleAccountChange, handleAmountExclVatChange, handleAmountInclVatChange, handleVatRateChange } =
    useReviewEntryLineItemCalculation({
      accounts,
    })

  const hasLineItems = lineItems.length > 0

  const handleAddLineClick = useCallback(() => {
    addLineItem(getDefaultReviewEntryLineItem() as ReviewEntryLineItemFormValues)
  }, [addLineItem])

  const handleDeleteLineClick = useCallback(
    (lineIndex: number) => {
      removeLineItem(lineIndex)
    },
    [removeLineItem],
  )

  return (
    <Styled.ReviewEntryLineItemsWrapper>
      <Styled.ReviewEntryLineItemsScrollWrapper>
        <Styled.ReviewEntryLineItemsTable role="table">
          <Styled.ReviewEntryLineItemsHeaderRow role="row">
            <Styled.ReviewEntryLineItemsHeaderCell role="columnheader">
              <Text variant="bodyMD" weight="semiBold">
                {t('review_entries.detail.line_items.header.account_number')}
              </Text>
            </Styled.ReviewEntryLineItemsHeaderCell>
            <Styled.ReviewEntryLineItemsHeaderCell role="columnheader">
              <Text variant="bodyMD" weight="semiBold">
                {t('review_entries.detail.line_items.header.label')}
              </Text>
            </Styled.ReviewEntryLineItemsHeaderCell>
            <Styled.ReviewEntryLineItemsHeaderCell role="columnheader">
              <Text variant="bodyMD" weight="semiBold">
                {t('review_entries.detail.line_items.header.vat')}
              </Text>
            </Styled.ReviewEntryLineItemsHeaderCell>
            <Styled.ReviewEntryLineItemsHeaderCell alignment="right" role="columnheader">
              <ReviewEntryLineItemsAmountHeader
                currencyId={currencyId}
                label={t('review_entries.detail.line_items.totals.excl_vat.label')}
              />
            </Styled.ReviewEntryLineItemsHeaderCell>
            <Styled.ReviewEntryLineItemsHeaderCell alignment="right" role="columnheader">
              <ReviewEntryLineItemsAmountHeader
                currencyId={currencyId}
                label={t('review_entries.detail.line_items.header.vat')}
              />
            </Styled.ReviewEntryLineItemsHeaderCell>
            <Styled.ReviewEntryLineItemsHeaderCell alignment="right" role="columnheader">
              <ReviewEntryLineItemsAmountHeader
                currencyId={currencyId}
                label={t('review_entries.detail.line_items.field.incl_vat.label')}
              />
            </Styled.ReviewEntryLineItemsHeaderCell>
          </Styled.ReviewEntryLineItemsHeaderRow>
          {lineItems.map((lineItem, lineIndex) => (
            <ReviewEntryLineItemRow
              isFormReady={isFormReady}
              isReadOnly={isReadOnly}
              key={lineItem.id}
              lineIndex={lineIndex}
              onAccountChange={handleAccountChange}
              onAmountExclVatChange={handleAmountExclVatChange}
              onAmountInclVatChange={handleAmountInclVatChange}
              onDeleteLine={handleDeleteLineClick}
              onVatRateChange={handleVatRateChange}
              showDeleteAction={!isReadOnly}
            />
          ))}
          {!isReadOnly && (
            <Styled.ReviewEntryLineItemsAddLineRow role="row">
              <Styled.ReviewEntryLineItemsAddLineCell aria-colspan={6} role="cell">
                <Button disabled={!isFormReady} icon="circlePlus" onClick={handleAddLineClick} variant="text">
                  <Text variant="bodySM" weight="bold">
                    {t('review_entries.detail.line_items.add_line')}
                  </Text>
                </Button>
              </Styled.ReviewEntryLineItemsAddLineCell>
            </Styled.ReviewEntryLineItemsAddLineRow>
          )}
        </Styled.ReviewEntryLineItemsTable>
        {hasLineItems && <ReviewEntryLineItemsTotals />}
      </Styled.ReviewEntryLineItemsScrollWrapper>
    </Styled.ReviewEntryLineItemsWrapper>
  )
}
